// HtmlInputTypes.jsx
import React from "react";

const HtmlInputTypes = () => {
  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", lineHeight: "1.6" }}>
      <h1>HTML Input Types - Comprehensive Guide</h1>

      {/* Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          HTML input elements support a wide range of types to collect different kinds of user
          data efficiently. Using the correct type improves validation, user experience, and
          accessibility.
        </p>
      </section>

      {/* Text */}
      <section id="text">
        <h2>2. Text Input</h2>
        <p>Single-line text input.</p>
        <pre>
          <code>
{`<label htmlFor="username">Username:</label>
<input type="text" id="username" name="username" placeholder="Enter your username" />`}
          </code>
        </pre>
      </section>

      {/* Password */}
      <section id="password">
        <h2>3. Password Input</h2>
        <p>Input hides characters for security.</p>
        <pre>
          <code>
{`<label htmlFor="password">Password:</label>
<input type="password" id="password" name="password" />`}
          </code>
        </pre>
      </section>

      {/* Email */}
      <section id="email">
        <h2>4. Email Input</h2>
        <p>Validates email addresses automatically.</p>
        <pre>
          <code>
{`<label htmlFor="email">Email:</label>
<input type="email" id="email" name="email" placeholder="example@mail.com" />`}
          </code>
        </pre>
      </section>

      {/* Number */}
      <section id="number">
        <h2>5. Number Input</h2>
        <p>Only numeric values allowed with min, max, and step attributes.</p>
        <pre>
          <code>
{`<label htmlFor="age">Age:</label>
<input type="number" id="age" name="age" min="1" max="100" step="1" />`}
          </code>
        </pre>
      </section>

      {/* Tel */}
      <section id="tel">
        <h2>6. Telephone Input</h2>
        <p>Input optimized for phone numbers.</p>
        <pre>
          <code>
{`<label htmlFor="phone">Phone:</label>
<input type="tel" id="phone" name="phone" placeholder="+1234567890" />`}
          </code>
        </pre>
      </section>

      {/* URL */}
      <section id="url">
        <h2>7. URL Input</h2>
        <p>Validates web addresses automatically.</p>
        <pre>
          <code>
{`<label htmlFor="website">Website:</label>
<input type="url" id="website" name="website" placeholder="https://example.com" />`}
          </code>
        </pre>
      </section>

      {/* Date */}
      <section id="date">
        <h2>8. Date Input</h2>
        <p>Select a date using a calendar picker.</p>
        <pre>
          <code>
{`<label htmlFor="birthday">Birthday:</label>
<input type="date" id="birthday" name="birthday" />`}
          </code>
        </pre>
      </section>

      {/* Time */}
      <section id="time">
        <h2>9. Time Input</h2>
        <p>Select time using a clock picker.</p>
        <pre>
          <code>
{`<label htmlFor="appt">Appointment Time:</label>
<input type="time" id="appt" name="appt" />`}
          </code>
        </pre>
      </section>

      {/* Color */}
      <section id="color">
        <h2>10. Color Input</h2>
        <p>Pick a color using a color picker.</p>
        <pre>
          <code>
{`<label htmlFor="favcolor">Favorite Color:</label>
<input type="color" id="favcolor" name="favcolor" value="#ff0000" />`}
          </code>
        </pre>
      </section>

      {/* Range */}
      <section id="range">
        <h2>11. Range Input</h2>
        <p>Select a value from a range using a slider.</p>
        <pre>
          <code>
{`<label htmlFor="volume">Volume:</label>
<input type="range" id="volume" name="volume" min="0" max="100" />`}
          </code>
        </pre>
      </section>

      {/* Checkbox */}
      <section id="checkbox">
        <h2>12. Checkbox Input</h2>
        <p>Select one or multiple options.</p>
        <pre>
          <code>
{`<input type="checkbox" id="subscribe" name="subscribe" value="newsletter" />
<label htmlFor="subscribe">Subscribe to newsletter</label>`}
          </code>
        </pre>
      </section>

      {/* Radio */}
      <section id="radio">
        <h2>13. Radio Input</h2>
        <p>Select only one option from a group.</p>
        <pre>
          <code>
{`<p>Gender:</p>
<input type="radio" id="male" name="gender" value="male" />
<label htmlFor="male">Male</label>
<input type="radio" id="female" name="gender" value="female" />
<label htmlFor="female">Female</label>`}
          </code>
        </pre>
      </section>

      {/* File */}
      <section id="file">
        <h2>14. File Input</h2>
        <p>Upload files.</p>
        <pre>
          <code>
{`<label htmlFor="resume">Upload Resume:</label>
<input type="file" id="resume" name="resume" />`}
          </code>
        </pre>
      </section>

      {/* Submit & Reset */}
      <section id="submit-reset">
        <h2>15. Submit & Reset Buttons</h2>
        <p>Submit sends form data; Reset clears the form.</p>
        <pre>
          <code>
{`<input type="submit" value="Submit" />
<input type="reset" value="Reset" />`}
          </code>
        </pre>
      </section>

      {/* Hidden */}
      <section id="hidden">
        <h2>16. Hidden Input</h2>
        <p>Stores data not visible to users.</p>
        <pre>
          <code>
{`<input type="hidden" name="userId" value="12345" />`}
          </code>
        </pre>
      </section>

      {/* Best Practices */}
      <section id="best-practices">
        <h2>17. Best Practices</h2>
        <ul>
          <li>Always use <strong>label</strong> for accessibility.</li>
          <li>Choose the correct input type for proper validation.</li>
          <li>Provide placeholder and title attributes to guide users.</li>
          <li>Group related inputs using <strong>fieldset</strong> and <strong>legend</strong>.</li>
        </ul>
      </section>
    </div>
  );
};

export default HtmlInputTypes;
